import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { CodeSentryFetcherName, fetcherToPlainText } from '../../../../../shared/fetchers';
import { FOSSABOT_SUPPORTED_FETCHERS } from '../../../../../shared/fossabot/common';
import { DependencyUsage, dependencyUsageFromDepth } from '../../../../../shared/issuesV2/common';
import { isProjectScope } from '../../../../../shared/issuesV2/helpers';
import { useStartGitHubAppInstall } from '../../../../hooks/api/vcs/githubApp/useStartGitHubAppInstall';
import { getErrorMessage } from '../../../../ReactQuery/errors';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import DetailsPanel from '../../../generic/DetailsPanel';
import VulnerabilityDrawerSection from '../../../shared/vulns/Drawer/Section';
import type { Fetcher } from '../../../../../shared/fetchers';

import { FossabotBlockedBox } from './FossabotBlockedBox';
import { FossabotConnectionBanner } from './FossabotConnectionBanner';
import { FossabotFixBox } from './FossabotFixBox';
import { FossabotNoFixBox } from './FossabotNoFixBox';
import { FossabotOtherFixesBox } from './FossabotOtherFixesBox';
import { FossabotProjectScopePrompt } from './FossabotProjectScopePrompt';
import { useFossabotConnection } from './useFossabotConnection';
import { useFossabotNoFix } from './useFossabotNoFix';
import { useFossabotPrFlow } from './useFossabotPrFlow';
import type { FossabotBlockedNotice } from './FossabotBlockedBox';
import type { FossabotPrFlow } from './useFossabotPrFlow';

/** Why Create PR is disabled when the repository is not connected; the banner's Connect button is the CTA. */
const NOT_CONNECTED_REASON = 'Connect fossabot to create PRs';

/** Why Create PR is not offered on a transitive dependency: fossabot only edits the manifest. */
const TRANSITIVE_NOTICE: FossabotBlockedNotice = {
  heading: 'PRs cannot be opened for transitive dependencies',
  description: 'This package is pulled in by another dependency, not declared directly in your project. To fix this, upgrade the direct dependency that includes it.',
};

/*
  TODO(CORE-7662): the copy on the four notices below is placeholder, waiting
  on design sign-off.
*/

/** Why Create PR is not offered on a CLI-uploaded project: there is no connected repository to open a PR on. */
const CLI_PROJECT_NOTICE: FossabotBlockedNotice = {
  heading: 'PRs cannot be opened for CLI projects',
  description: 'This project was uploaded with the FOSSA CLI, so fossabot has no connected repository to open a PR on.',
};

/** Why Create PR is not offered on a snippet match: the code was copied in, not declared in a manifest. */
const SNIPPET_NOTICE: FossabotBlockedNotice = {
  heading: 'PRs cannot be opened for snippets',
  description: 'This code was matched as a snippet, not declared in a manifest, so fossabot has nothing to upgrade.',
};

/** Why Create PR is not offered on a vendored dependency: same as a snippet, there is no manifest entry to bump. */
const VENDORED_NOTICE: FossabotBlockedNotice = {
  heading: 'PRs cannot be opened for vendored dependencies',
  description: 'This dependency is checked in to your code, not declared in a manifest, so fossabot has nothing to upgrade.',
};

/** Why Create PR is not offered on a manually added dependency: it was added in FOSSA, so no manifest declares it. */
const MANUAL_NOTICE: FossabotBlockedNotice = {
  heading: 'PRs cannot be opened for manually added dependencies',
  description: 'This dependency was manually added in FOSSA, not declared in a manifest, so fossabot has nothing to upgrade.',
};

/** Vendored dependencies are uploaded by the CLI as archives. */
const VENDORED_FETCHER = 'archive';

/** Manually added dependencies get the user fetcher. */
const MANUAL_FETCHER = 'user';

/** Why Create PR is not offered for a dependency whose ecosystem fossabot does not support. */
const getUnsupportedEcosystemNotice = (fetcher: string): FossabotBlockedNotice => {
  const ecosystem = fetcherToPlainText(fetcher as Fetcher) ?? fetcher;
  return {
    heading: `${ecosystem} dependencies are not supported`,
    description: `fossabot cannot open PRs for ${ecosystem} packages. Review the supported ecosystems documentation below.`,
    hasEcosystemsLink: true,
  };
};

/** The fix cards still render when not connected, in their idle state with Create PR disabled. */
const NOT_CONNECTED_FLOW: FossabotPrFlow = {
  status: 'none',
  vcsUrl: null,
  fossabotUrl: null,
  onCreatePR: () => undefined,
};

/**
 * The connection banner and the fix cards. Only mounts once nothing blocks a
 * PR, so the connection and PR status reads never run for a dependency
 * fossabot cannot touch.
 *
 * While the connection status is loading, the banner shows alone. Not
 * connected, or the status check failed and we cannot tell: the same fix
 * cards, with Create PR disabled and the banner's Connect button as the CTA.
 */
const FossabotPrSection: VoidFunctionComponent<{ projectLocator: string }> = ({ projectLocator }) => {
  const { mutate: startInstall } = useStartGitHubAppInstall();
  const connection = useFossabotConnection(projectLocator);
  const isConnected = connection.state === 'connected';
  const prFlow = useFossabotPrFlow(isConnected);

  const flow = isConnected ? prFlow : NOT_CONNECTED_FLOW;
  const disabledReason = isConnected ? undefined : NOT_CONNECTED_REASON;

  return (
    <>
      <FossabotConnectionBanner
        state={connection.state}
        errorMessage={getErrorMessage(connection.error, 'Could not check fossabot connection status.')}
        onConnect={() => startInstall('fossabot')}
      />
      {connection.state !== 'checking' && flow && (
        <>
          <FossabotFixBox
            status={flow.status}
            vcsUrl={flow.vcsUrl}
            fossabotUrl={flow.fossabotUrl}
            onCreatePR={flow.onCreatePR}
            errorMessage={flow.errorMessage}
            onRetry={flow.onRetry}
            disabledReason={disabledReason}
          />
          <FossabotOtherFixesBox onCreatePR={flow.onCreatePR} disabledReason={disabledReason} />
        </>
      )}
    </>
  );
};

/**
 * The fossabot remediation panel, shown for vulnerability issues when the
 * fossabotDepUpgradePRs flag is on and the user can view fossabot upgrades.
 * Renders the collapsible Remediation section with the fossabot connection
 * banner bleeding edge to edge across the panel.
 */
type Props = {
  /** Switches the drawer to the Projects tab, where each affected project links to project scope. */
  onViewProjects: () => void;
};

/**
 * What the Remediation section shows: the first case that stops fossabot from
 * opening a PR, else the connection banner and fix cards.
 *
 * A fossabot PR targets exactly one repository, so outside project scope
 * (global / release group) the user is pointed at project scope. An issue with
 * no package manager, a project the issue does not list, or an unknown depth
 * all fail open to the fix cards.
 */
const FossabotRemediationContent: VoidFunctionComponent<Props> = ({ onViewProjects }) => {
  const scope = useSelector(IssuesV2Store.selectors.getScope);
  const issue = useSelector(IssuesV2Store.selectors.getIssueDetails);
  const noFixType = useFossabotNoFix();

  if (!isProjectScope(scope)) {
    return <FossabotProjectScopePrompt onViewProjects={onViewProjects} />;
  }

  const fetcher = issue?.source?.packageManager;
  const depth = issue?.projects.find(({ id }) => id === scope.id)?.depth ?? null;

  if (scope.id.startsWith('custom+')) {
    return <FossabotBlockedBox notice={CLI_PROJECT_NOTICE} />;
  }
  if (fetcher === VENDORED_FETCHER) {
    return <FossabotBlockedBox notice={VENDORED_NOTICE} />;
  }
  if (fetcher === MANUAL_FETCHER) {
    return <FossabotBlockedBox notice={MANUAL_NOTICE} />;
  }
  if (fetcher && !FOSSABOT_SUPPORTED_FETCHERS.has(fetcher)) {
    return <FossabotBlockedBox notice={getUnsupportedEcosystemNotice(fetcher)} />;
  }
  if (dependencyUsageFromDepth(depth) === DependencyUsage.Deep) {
    return <FossabotBlockedBox notice={TRANSITIVE_NOTICE} />;
  }
  if (issue?.analysisSnippetId) {
    return <FossabotBlockedBox notice={SNIPPET_NOTICE} />;
  }
  if (noFixType !== null) {
    return <FossabotNoFixBox type={noFixType} />;
  }
  return <FossabotPrSection projectLocator={scope.id} />;
};

export const FossabotRemediationPanel: VoidFunctionComponent<Props> = ({ onViewProjects }) => {
  const issue = useSelector(IssuesV2Store.selectors.getIssueDetails);

  /*
    Matches the sibling sections (Remediation, Header): with no issue in Redux
    there is nothing to remediate, so render nothing rather than an empty card.
    Binary decomposition (csbinary) dependencies never had a Remediation
    section, so keep hiding it rather than showing an unsupported notice.
  */
  if (!issue || issue.source?.packageManager === CodeSentryFetcherName) {
    return null;
  }

  return (
    <VulnerabilityDrawerSection>
      <DetailsPanel id="fossabot-remediation" target="Remediation" isUnpadded>
        <div data-testid="fossabot-remediation-panel">
          <FossabotRemediationContent onViewProjects={onViewProjects} />
        </div>
      </DetailsPanel>
    </VulnerabilityDrawerSection>
  );
};
